import { FC } from 'react';
import { useSelector } from 'react-redux';

import { Loader } from 'src/components/Loader/Loader';
import { Panel } from 'src/components/Panel/Panel';
import { getCurrentParkingPlace } from 'src/store/parking/selectors';
import { getUserPermissions } from 'src/store/user/selectors';
import { Parking } from 'src/types';

import { ReservedBySpaceInfo } from './ReservedBySpaceInfo';
import { AdminBooking, AdminBookingFields } from './panels/AdminBooking';
import { BookActions } from './panels/BookActions';
import { BusyBooking, BusyBookingFields, UnbookActions } from './panels/Busy';
import { GuestInfo } from './panels/GuestInfo';
import { RegularBooking } from './panels/RegularBooking';
import { ReservedByEmployee } from './panels/ReservedByEmployee/ReservedByEmployee';
import { ReservedByEmployeeInfo } from './panels/ReservedByEmployeeInfo';

interface InnerProps {
  parking: Parking;
  employeeNumber: string;
  adminSpaces: Set<number>;
  spaces: Set<number>;
}

const Inner: FC<InnerProps> = ({
  parking,
  employeeNumber,
  adminSpaces,
  spaces,
}) => {
  const isAdmin = !!adminSpaces.size;
  const isAdminOfSpace =
    isAdmin && (!parking.spaceId || adminSpaces.has(parking.spaceId));
  const isReservedByEmployee = !!parking.responsibleEmployeeNumber;
  const isReservedBySpace = !!parking.spaceId && !spaces.has(parking.spaceId);
  const isShared = parking.shared;
  const isGuest = !isAdmin && parking.guest;
  const hasPermissionToUnbook =
    parking.status &&
    (parking.status.authorEmployeeNumber === employeeNumber ||
      parking.status.responsibleEmployeeNumber === employeeNumber ||
      isAdminOfSpace);

  if (isAdmin && isReservedByEmployee) {
    return (
      <ReservedByEmployee
        parking={parking}
        employeeNumber={employeeNumber}
        isAdminOfSpace={isAdminOfSpace}
      />
    );
  }

  if (parking.status)
    return (
      <BusyBooking>
        <Panel actions={hasPermissionToUnbook ? <UnbookActions /> : null}>
          <BusyBookingFields status={parking.status} />
        </Panel>
      </BusyBooking>
    );

  if (isAdmin) {
    return (
      <AdminBooking>
        <Panel actions={<BookActions />}>
          <AdminBookingFields parking={parking} />
        </Panel>
      </AdminBooking>
    );
  }

  if (isReservedByEmployee && !isShared) {
    return (
      <Panel>
        <ReservedByEmployeeInfo
          responsible={parking.responsibleEmployeeNumber!}
        />
      </Panel>
    );
  }

  if (isReservedBySpace) {
    return (
      <Panel>
        <ReservedBySpaceInfo
          spaceId={parking.spaceId!}
          spaceAdministrator={parking.spaceAdministrator}
        />
      </Panel>
    );
  }

  if (isGuest) {
    return (
      <Panel>
        <GuestInfo guest spaceAdministrator={parking.spaceAdministrator} />
      </Panel>
    );
  }

  return (
    <RegularBooking
      employeeNumber={employeeNumber}
      guest={parking.guest}
      status={parking.status}
    />
  );
};

export const ParkingBookingSidesheetPanel: FC = () => {
  const parking = useSelector(getCurrentParkingPlace);
  const data = useSelector(getUserPermissions);

  if (!data?.employeeNumber) return null;

  if (!parking) return <Loader />;

  return (
    <Inner
      parking={parking}
      employeeNumber={data.employeeNumber}
      spaces={data.spaces}
      adminSpaces={data.adminSpaces}
    />
  );
};
